import { Location } from '@angular/common';
import { Component, ElementRef, NgZone, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
  ClientIframePackageDataModel,
  ELang,
  IframeCategoryItem,
  isElementInViewport,
  isElementToLeft,
  isElementToRight,
  ISettingsMenu,
  LayoutProductType,
  OptionsIframeModel,
  ProductListItemModelCPB,
} from '@atlas-workspace/shared/models';
import {
  ModalFacadeService,
  PicarioImageService,
  PicarioService,
  RestTranslateService,
  SharedUiStorageService,
  StateTranslateService,
} from '@atlas-workspace/shared/service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { delay, filter, forkJoin, map, Observable, skip, switchMap, take, tap } from 'rxjs';

import { DownloadModalComponent } from './download-modal/download-modal.component';
import { OptionsIframeService } from './services/options-iframe.service';
import { ShareModalComponent } from './share-modal/share-modal.component';
import { TranslateService } from '@ngx-translate/core';

@UntilDestroy()
@Component({
  selector: 'atl-options-iframe',
  templateUrl: './options-iframe.component.html',
  styleUrls: ['./options-iframe.component.scss'],
})
export class OptionsIframeComponent implements OnInit {
  @ViewChild('scrollElement') scrollElement!: ElementRef;
  public activeTab!: string;
  public navMenu!: ISettingsMenu[];
  public totalPrice = 0;
  public isSummaryPage = false;
  public showPrices = true;
  public readonly visibility = {
    firstCategory: true,
    lastCategory: true,
  };
  public lastScrolledIndex = -1;
  readonly truncateLimit = 30;

  private tokenId!: string;
  private projectId!: string;
  private selectionToken = '';
  private imageUrl = '';
  private overlayImages: { url: string; typeId: number; id: number }[] = [];
  private sceneImages: { url: string; zIndex: number; id: number; typeId: number | null }[] = [];
  private imageUrls: string[] = [];
  private categories!: IframeCategoryItem[];
  private locale = 'no';
  private packageData!: ClientIframePackageDataModel;

  constructor(
    private readonly optionsIframeService: OptionsIframeService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    public readonly stateTranslateService: StateTranslateService,
    private readonly modalFacadeService: ModalFacadeService,
    private readonly sharedUiStorageService: SharedUiStorageService,
    private readonly location: Location,
    private readonly picarioImageService: PicarioImageService,
    private readonly picarioService: PicarioService,
    private readonly ngZone: NgZone,
    private readonly restTranslateService: RestTranslateService,
    private readonly translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.getSelectionToken();
    this.listenPakagesUpdates();
    this.listenSelectionToken();
  }

  private initializeData(): void {
    this.stateTranslateService.onLoadedLocalizationData.pipe(untilDestroyed(this));
    this.tokenId = this.route?.snapshot.params['tokenId'];
    this.projectId = this.route?.snapshot.params['projectId'];
    this.getCategories();
    this.isSummaryPage = this.router.url.includes('summary');
    this.listenNavigationEndEvents();
    this.setNavMenu();
    this.getTotalPrice();
    this.getShowPrices();
  }

  onCategorySelect(): void {
    this.isSummaryPage = false;
  }

  private listenPakagesUpdates(): void {
    this.optionsIframeService.packages$.pipe(
      untilDestroyed(this),
      filter((data): data is ClientIframePackageDataModel => data !== null),
      skip(1),
      tap((data) => {
        this.packageData = data;
        const selectedPackage = data.packages.find(x => x.selected);
        if (selectedPackage) {
          this.navMenu.forEach(x => {
            x.disabled = false;
          });
        }
      }),
      filter((data): data is ClientIframePackageDataModel =>
        !!data.optionDesignLineProducts &&
        Array.isArray(data.packages) &&
        data.packages.length > 0
      ),
      switchMap(() => this.optionsIframeService.selectionToken$.pipe(take(1))),
      filter(token => !!token),
      switchMap(token =>
        this.optionsIframeService.getIframeCategories(this.tokenId, token).pipe(
          take(1),
          tap((categories: IframeCategoryItem[]) => {
            const packageNav = {
              disabled: false,
              name: this.translateService.instant('Title.Design_lines'),
              route: `/options/project/${this.projectId}/token/${this.tokenId}/packages`
            };
            let menu: ISettingsMenu[] = categories.map((x) => ({
              id: x.id,
              name: x.name,
              disabled: false,
              route:
                `/options/project/${this.projectId}/token/${this.tokenId}/category/${x.id}` + (x.is_introduction ? '/introduction' : ''),
            }));
            menu = this.insertNavItemAfterIntroduction(menu, packageNav);
            this.optionsIframeService.setNavMenu(menu);
            const firstValidItem = menu.find(item =>
              !item.route.includes('introduction') &&
              !item.route.includes('packages')
            );
            void this.router.navigate([firstValidItem?.route]);

          })
        )
      )
    ).subscribe();
  }



  openSummary(): void {
    if (this.disabledSummary) {
      return;
    }
    this.isSummaryPage = true;
    void this.router.navigate([`/options/project/${this.projectId}/token/${this.tokenId}/summary`]);
  }

  openDownloadModal(): void {
    const modalRef = this.modalFacadeService.openModal(DownloadModalComponent, {
      centered: true,
      modalDialogClass: 'br-24-modal',
      ...this.sharedUiStorageService.modalImportProductConfig,
    });

    modalRef.componentInstance.saveChanges.pipe(untilDestroyed(this)).subscribe((email: string) => {
      this.saveIframeSelectedItems(email);
    });
  }

  public scrollLeft(): void {
    const container = this.scrollElement.nativeElement;
    const items = container.querySelectorAll('.simple-tabs__item');

    if (this.lastScrolledIndex <= 0) this.lastScrolledIndex = 5;
    for (let i = this.lastScrolledIndex - 1; i >= 0; i--) {
      const item = items[i] as HTMLElement;
      if (!isElementInViewport(container, item) && isElementToLeft(container, item)) {
        const scrollPosition = item.offsetLeft - 50;
        container.scrollTo({ left: scrollPosition, behavior: 'smooth' });
        this.lastScrolledIndex = i;
        break;
      }
    }
  }

  onScroll(): void {
    this.ngZone.onStable.pipe(take(1), delay(200)).subscribe(() => {
      if (this.visibility.firstCategory) {
        this.lastScrolledIndex = 0;
      }
      if (this.visibility.lastCategory) {
        this.lastScrolledIndex = this.navMenu.length - 1;
      }
    });
  }

  public scrollRight(): void {
    const container = this.scrollElement.nativeElement;
    const items = container.querySelectorAll('.simple-tabs__item');
    for (let i = this.lastScrolledIndex + 1; i < items.length; i++) {
      const item = items[i] as HTMLElement;
      if (!isElementInViewport(container, item) && isElementToRight(container, item)) {
        const scrollPosition = item.offsetLeft + item.offsetWidth - container.offsetWidth + 50;
        container.scrollTo({ left: scrollPosition, behavior: 'smooth' });
        this.lastScrolledIndex = i;
        break;
      }
    }
  }

  setArrowsVisibility(val: boolean[]): void {
    if (val[0]) {
      this.visibility.firstCategory = val[1];
    } else {
      this.visibility.lastCategory = val[1];
    }
  }

  openShareModal(): void {
    const modalRef = this.modalFacadeService.openModal(ShareModalComponent, {
      centered: true,
      modalDialogClass: 'br-24-modal',
      ...this.sharedUiStorageService.modalImportProductConfig,
    });
    modalRef.componentInstance.selectionToken = this.selectionToken;
    modalRef.componentInstance.tokenId = this.tokenId;
    modalRef.componentInstance.navRoute = this.navMenu[0].route;
    modalRef.componentInstance.saveChanges.pipe(untilDestroyed(this)).subscribe((email: string) => {
      this.saveIframeSelectedItems(email);
    });
  }

  private saveIframeSelectedItems(email = ''): void {
    this.optionsIframeService.categoriesData$.pipe(take(1)).subscribe((res: IframeCategoryItem[]) => {
      const selectedItems = res
        .flatMap((cat) => cat.products ?? [])
        .filter((product) => product)
        .map(({ layoutProductTypeId, id }) => ({
          layout_product_type_id: layoutProductTypeId,
          layout_product_type_item_id: id,
        }));

      this.optionsIframeService
        .saveIframeSelectedItems(this.tokenId, email, selectedItems ?? [], this.selectionToken)
        .pipe(take(1))
        .subscribe((token: string) => {
          this.selectionToken = token;
        });
    });
  }

  private getShowPrices(): void {
    this.optionsIframeService.showPrices$.pipe(untilDestroyed(this)).subscribe((showPrices: boolean) => {
      this.showPrices = showPrices;
    });
  }

  private listenNavigationEndEvents(): void {
    this.router.events
      .pipe(
        untilDestroyed(this),
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      )
      .subscribe(() => {
        this.isSummaryPage = this.router.url.includes('summary');
      });
  }

  private getCategories(): void {
    this.optionsIframeService
      .getIframeCategories(this.tokenId, this.selectionToken)
      .pipe(
        take(1),
        tap((categories: IframeCategoryItem[]) => {
          this.categories = categories;
          const intro = this.categories.find((x) => x.is_introduction);
          if (intro) {
            this.optionsIframeService.setIntroduction(intro);
          }
          this.optionsIframeService.setCategories(categories, +this.projectId, this.tokenId);
          this.initPackages();
        }),
        switchMap((categories: IframeCategoryItem[]) => {
          const loadObservables: Observable<void>[] = categories.map((category) => this.loadAndCacheTypes(category.id));

          return loadObservables.length ? forkJoin(loadObservables) : [];
        }),
      )
      .subscribe(() => {
        this.optionsIframeService.updateCategories(this.categories);
        this.calculateAndSaveTotalPrice();
      });
  }

  get disabledSummary(): boolean {
    if (!this.packageData) {
      return false;
    }

    if (this.packageData.optionDesignLine && !this.packageData.optionDesignLineProducts) {
      const selectedPackage = this.packageData.packages.find(x => x.selected);
      return !selectedPackage;
    }

    return false;
  }


  private initPackages(): void {
    this.optionsIframeService.getIframePackages(this.tokenId, this.selectionToken).pipe(take(1))
      .subscribe((res: ClientIframePackageDataModel) => {
        this.packageData = res;
        if (this.navMenu && this.packageData.packages?.length) {
          const packageTitle = this.packageData.optionDesignLine || this.packageData.optionDesignLineProducts ? 'Title.Design_lines' : 'Entity.Packages';
          const packageNav = {
            disabled: false,
            name: `${this.translateService.instant(packageTitle)}`,
            route: `/options/project/${this.projectId}/token/${this.tokenId}/packages`
          }
          this.navMenu = this.insertNavItemAfterIntroduction(this.navMenu, packageNav);
          this.optionsIframeService.setNavMenu(this.navMenu);

          if (this.packageData.optionDesignLine && !this.packageData.optionDesignLineProducts) {
            const selectedPackage = this.packageData.packages.find(x => x.selected);
            if (!selectedPackage) {
              this.navMenu.forEach(m => {
                m.disabled = !m.route.includes('packages') && !m.route.includes('introduction');
              });
            }
          }

        }
      });
  }

  private insertNavItemAfterIntroduction(navMenu: ISettingsMenu[], itemToInsert: ISettingsMenu): ISettingsMenu[] {
    const firstNavItem = navMenu[0];
    const insertIndex = firstNavItem?.route?.includes('introduction') ? 1 : 0;
    const updatedNav = [...navMenu];
    updatedNav.splice(insertIndex, 0, itemToInsert);
    return updatedNav;
  }


  private calculateAndSaveTotalPrice(): void {
    this.categories.forEach((cat) => {
      const selectedPicarioProducts = cat.categoryDetails?.layoutProductTypes
        .flatMap((productType) => productType.products)
        .filter((x) => x?.selected);
      const totalPrice = selectedPicarioProducts?.reduce((total, x) => total + Number(x.price), 0);
      cat.totalPrice = totalPrice ?? 0;
    });

    const totalCategoriesPrice = this.categories.reduce((total, x) => total + Number(x.totalPrice || 0), 0);
    this.optionsIframeService.setTotalPrice(totalCategoriesPrice);
  }

  private loadAndCacheTypes(categoryId: number): Observable<void> {
    return this.optionsIframeService.getCategoryDetails(this.tokenId, categoryId, this.selectionToken).pipe(
      take(1),
      tap((res: OptionsIframeModel) => {
        const category = this.categories.find((x) => x.id === res.id);
        if (category) {
          category.categoryDetails = res;
          const allProducts = category.categoryDetails?.layoutProductTypes?.flatMap((pt) => pt.products);
          if (!this.selectionToken) {
            allProducts.forEach((pr) => {
              if (pr) {
                pr.selected = pr?.standard;
              }
            });
          }
          const selectedProducts = allProducts.filter((product) => product?.selected);
          category.products = selectedProducts;
        }

        res.layoutProductTypes
          .flatMap((pt) => pt.products)
          .filter((product) => product?.picarioData)
          .forEach((product) => {
            const activeType = res.layoutProductTypes.find((x) => x.id === product.layoutProductTypeId);
            if (activeType) {
              this.updateProductImage(product, activeType);
            }
          });

        this.picarioService.preloadImages(this.imageUrls);
      }),
      map(() => undefined),
    );
  }

  private updateProductImage(product: ProductListItemModelCPB, type: LayoutProductType): void {
    this.imageUrl = this.picarioImageService.updateProductImage(
      product,
      type,
      this.overlayImages,
      this.sceneImages,
      this.imageUrls,
    );
  }

  private setNavMenu(): void {
    this.optionsIframeService.optionCategories$
      .pipe(
        filter((x) => !!x.length),
        untilDestroyed(this)
      )
      .subscribe((res: ISettingsMenu[]) => {
        this.navMenu = res;
      });
  }

  private getTotalPrice(): void {
    this.optionsIframeService.totalPrice$.pipe(untilDestroyed(this)).subscribe((price: number) => {
      this.totalPrice = price;
    });
  }

  private getSelectionToken(): void {
    const selectionToken = this.route.snapshot.queryParamMap.get('selection_token');
    const locale = this.route.snapshot.queryParamMap.get('lang');
    if (locale) {
      this.locale = locale;
      this.restTranslateService.setLocalStorageLocalize(this.locale as ELang);
      this.optionsIframeService.locale = this.locale;
    }
    if (selectionToken) {
      this.selectionToken = selectionToken;
      this.optionsIframeService.setSelectionToken(this.selectionToken);
      const newUrl = this.removeQueryParam('selection_token');
      this.location.replaceState(newUrl);
    }
    this.initializeData();
  }

  private removeQueryParam(param: string): string {
    const url = new URL(window.location.href);
    url.searchParams.delete(param);
    return url.pathname + url.search;
  }

  private listenSelectionToken(): void {
    this.optionsIframeService.selectionToken$.pipe(
      filter(x => !!x),
      untilDestroyed(this)
    )
      .subscribe(token => {
        this.selectionToken = token;
      });
  }
}
